import type { ExpenseCategoryDto } from "@pilatestime/shared-types";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Lock } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { fetchExpenseCategoriesQueryOptions } from "@/api/orgMgmtCostManagement";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from "@/components/ui/tooltip";
import { useApiParameters } from "@/hooks/useApiParameters";
import { useArchiveCategory } from "@/hooks/useCostManagementMutations";
import { OrganizationManagementNavigation } from "@/routes/_auth/app/_app/org/$orgId/org-management/-components/OrganizationManagementNavigation";
import { CategoryFormDialog } from "../-components/CategoryFormDialog";
import { CostManagementSubNav } from "../-components/CostManagementSubNav";

export const Route = createFileRoute(
	"/_auth/app/_app/org/$orgId/org-management/cost-management/categories/",
)({
	loader: async ({ context, params }) => {
		const { userId, token } = useApiParameters(
			context.userId,
			await context.get_token(),
		);
		const orgId = params.orgId;
		context.queryClient.ensureQueryData(
			fetchExpenseCategoriesQueryOptions(orgId, token),
		);
		return { userId, token, orgId };
	},
	component: CategoriesPage,
});

function CategoriesPage() {
	const { t } = useTranslation();
	const { token, orgId } = Route.useLoaderData();

	const categoriesQuery = useQuery(
		fetchExpenseCategoriesQueryOptions(orgId, token),
	);
	const archiveMutation = useArchiveCategory();

	const [createOpen, setCreateOpen] = useState(false);
	const [editTarget, setEditTarget] = useState<ExpenseCategoryDto | null>(null);

	return (
		<div className="flex flex-col h-full">
			<PageHeader
				title={t("orgManagement.costManagement.category.pageTitle")}
				description={t(
					"orgManagement.costManagement.subtitleVariants.categories",
				)}
				showBackButton={true}
				backButtonText={t("orgManagement.back")}
				hideOnMobile={false}
			/>
			<div className="flex-1 flex h-[calc(100vh-4rem)] min-h-0 overflow-hidden">
				<OrganizationManagementNavigation activeTab="cost-management" />
				<div className="flex-1 p-3 md:p-4 bg-base-200 min-h-0 overflow-auto">
					<div className="space-y-4 max-w-6xl mx-auto">
						<CostManagementSubNav orgId={orgId} active="categories" />

						<Card>
							<CardContent className="p-3 md:p-4 space-y-3">
								<div className="flex items-center justify-end">
									<Button
										onClick={() => setCreateOpen(true)}
										className="w-full sm:w-auto"
									>
										{t("orgManagement.costManagement.category.create")}
									</Button>
								</div>

								{categoriesQuery.isLoading && (
									<div className="text-sm text-base-content/60">
										{t("orgManagement.costManagement.loading")}
									</div>
								)}
								{categoriesQuery.data && (
									<TooltipProvider delayDuration={150}>
										<div className="md:hidden space-y-2">
											{categoriesQuery.data.map((c) => (
												<CategoryMobileCard
													key={c.id}
													category={c}
													onEdit={() => setEditTarget(c)}
													onArchive={() =>
														archiveMutation.mutate({
															token,
															organizationId: orgId,
															categoryId: c.id,
														})
													}
													isArchiving={archiveMutation.isPending}
												/>
											))}
											{categoriesQuery.data.length === 0 && (
												<div className="text-center py-8 text-sm text-base-content/60">
													{t("orgManagement.costManagement.empty")}
												</div>
											)}
										</div>
										<div className="hidden md:block">
											<Table>
												<TableHeader>
													<TableRow>
														<TableHead>
															{t(
																"orgManagement.costManagement.category.columns.color",
															)}
														</TableHead>
														<TableHead>
															{t(
																"orgManagement.costManagement.category.columns.name",
															)}
														</TableHead>
														<TableHead>
															{t(
																"orgManagement.costManagement.category.columns.status",
															)}
														</TableHead>
														<TableHead>
															{t(
																"orgManagement.costManagement.category.columns.actions",
															)}
														</TableHead>
													</TableRow>
												</TableHeader>
												<TableBody>
													{categoriesQuery.data.map((c) => (
														<TableRow
															key={c.id}
															className="cursor-pointer"
															onClick={() => setEditTarget(c)}
														>
															<TableCell>
																<span
																	className="inline-block h-4 w-4 rounded-full"
																	style={{ backgroundColor: c.color }}
																/>
															</TableCell>
															<TableCell>
																<span className="inline-flex items-center gap-2">
																	{c.systemKey && (
																		<Tooltip>
																			<TooltipTrigger asChild>
																				<button
																					type="button"
																					onClick={(ev) => ev.stopPropagation()}
																					aria-label={t(
																						"orgManagement.costManagement.category.systemTooltip",
																					)}
																					className="inline-flex"
																				>
																					<Lock className="h-3 w-3 text-base-content/60" />
																				</button>
																			</TooltipTrigger>
																			<TooltipContent>
																				{t(
																					"orgManagement.costManagement.category.systemTooltip",
																				)}
																			</TooltipContent>
																		</Tooltip>
																	)}
																	{c.systemKey
																		? t(
																				`orgManagement.costManagement.category.systemKey.${c.systemKey}`,
																			)
																		: c.name}
																</span>
															</TableCell>
															<TableCell>
																{c.archivedAt
																	? t(
																			"orgManagement.costManagement.category.archivedLabel",
																		)
																	: t(
																			"orgManagement.costManagement.category.activeLabel",
																		)}
															</TableCell>
															<TableCell onClick={(ev) => ev.stopPropagation()}>
																{!c.systemKey && !c.archivedAt && (
																	<Button
																		variant="ghost"
																		size="sm"
																		className="text-error hover:bg-error/10"
																		onClick={() =>
																			archiveMutation.mutate({
																				token,
																				organizationId: orgId,
																				categoryId: c.id,
																			})
																		}
																		disabled={archiveMutation.isPending}
																	>
																		{t(
																			"orgManagement.costManagement.category.archive.action",
																		)}
																	</Button>
																)}
															</TableCell>
														</TableRow>
													))}
												</TableBody>
											</Table>
										</div>
									</TooltipProvider>
								)}
							</CardContent>
						</Card>
					</div>
				</div>
			</div>

			<CategoryFormDialog
				open={createOpen}
				onOpenChange={setCreateOpen}
				token={token}
				orgId={orgId}
			/>
			<CategoryFormDialog
				open={!!editTarget}
				onOpenChange={(open) => {
					if (!open) setEditTarget(null);
				}}
				token={token}
				orgId={orgId}
				category={editTarget}
			/>
		</div>
	);
}

interface CategoryMobileCardProps {
	category: ExpenseCategoryDto;
	onEdit: () => void;
	onArchive: () => void;
	isArchiving: boolean;
}

function CategoryMobileCard({
	category,
	onEdit,
	onArchive,
	isArchiving,
}: CategoryMobileCardProps) {
	const { t } = useTranslation();
	const displayName = category.systemKey
		? t(`orgManagement.costManagement.category.systemKey.${category.systemKey}`)
		: category.name;
	const canArchive = !category.systemKey && !category.archivedAt;

	return (
		<button
			type="button"
			onClick={onEdit}
			className="w-full text-left rounded-lg border border-base-300 bg-base-100 p-3 flex items-center gap-3 hover:bg-base-200/40 transition-colors"
		>
			<span
				className="inline-block h-6 w-6 rounded-full shrink-0 border border-base-300"
				style={{ backgroundColor: category.color }}
			/>
			<div className="min-w-0 flex-1">
				<div className="font-medium flex items-center gap-1.5 truncate">
					{category.systemKey && (
						<Lock className="h-3 w-3 text-base-content/60 shrink-0" />
					)}
					<span className="truncate">{displayName}</span>
				</div>
				<div className="text-xs text-base-content/60">
					{category.archivedAt
						? t("orgManagement.costManagement.category.archivedLabel")
						: t("orgManagement.costManagement.category.activeLabel")}
				</div>
			</div>
			{canArchive && (
				<Button
					variant="ghost"
					size="sm"
					className="text-error hover:bg-error/10 shrink-0"
					onClick={(ev) => {
						ev.stopPropagation();
						onArchive();
					}}
					disabled={isArchiving}
				>
					{t("orgManagement.costManagement.category.archive.action")}
				</Button>
			)}
		</button>
	);
}
